import React, { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useGoogleOAuth } from "./hooks/useGoogleOAuth";
import { useMicrosoftOAuth } from "./hooks/useMicrosoftOAuth";
import { SupabaseAuthProvider } from "@/hooks/useSupabaseAuth";
import { useActivityHeartbeat } from "@/hooks/useActivityHeartbeat";
import { features } from "@/lib/features";
import { VerticalProvider } from "@/context/VerticalContext";
import { RoleViewProvider } from "@/context/RoleViewContext";
import { TenantProvider } from "@/context/TenantContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FeedbackProvider } from "@/context/FeedbackContext";
import { FeedbackWidget } from "@/components/feedback/FeedbackWidget";
import { InactivityTimeoutProvider } from "@/components/inactivity/InactivityTimeoutProvider";
import { initSentry } from "@/lib/sentry";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Initialize Sentry error tracking
initSentry();

// Lazy load heavy components for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const RoleRouter = lazy(() => import("@/components/layout/RoleRouter").then(m => ({ default: m.RoleRouter })));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
import { RoleRouteGuard } from '@/components/RoleRouteGuard';
import { ThemeProvider } from '@/context/ThemeContext';
const TeamPage = lazy(() => import("./pages/dashboard/TeamPage"));
const TeamViewPage = lazy(() => import("./pages/dashboard/TeamViewPage"));
const TeamDocumentsPage = lazy(() => import("./pages/dashboard/TeamDocumentsPage"));
const CalendarPage = lazy(() => import("./pages/dashboard/CalendarPage"));
const TrainingHub = lazy(() => import("./pages/dashboard/TrainingHub"));
const ChatPage = lazy(() => import("./pages/dashboard/ChatPage"));
const SalesPage = lazy(() => import("./pages/dashboard/SalesPage"));
const PipelinePage = lazy(() => import("./pages/dashboard/recruiting/PipelinePage"));
const RecruitingChat = lazy(() => import("./pages/dashboard/recruiting/RecruitingChat"));
const Leaderboard = lazy(() => import("./pages/dashboard/recruiting/Leaderboard"));
const TeamManagementPage = lazy(() => import("./pages/dashboard/recruiting/TeamManagementPage"));
const TeamMemberDetailPage = lazy(() => import("./pages/dashboard/recruiting/TeamMemberDetailPage"));
const RecruitmentTreePage = lazy(() => import("./pages/dashboard/recruiting/RecruitmentTreePage"));


const ImportPage = lazy(() => import("./pages/admin/ImportPage"));
const TemplateRequestsPage = lazy(
  () => import("./pages/admin/TemplateRequestsPage"),
);
const BulkOnboardPage = lazy(() => import("./pages/admin/BulkOnboardPage"));
const EmailTestPage = lazy(() => import("./pages/admin/EmailTestPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog"));
const MessageHistoryPage = lazy(() => import("./pages/admin/MessageHistoryPage"));
const FeedbackManagement = lazy(() => import("./pages/admin/FeedbackManagement"));
const TenantsPage = lazy(() => import("./pages/admin/tenants/index"));
const BillingManagementPage = lazy(() => import("./pages/admin/BillingManagementPage"));
const TenantBillingDetailPage = lazy(() => import("./pages/admin/TenantBillingDetailPage"));
const IspManagementPage = lazy(() => import("./pages/admin/IspManagementPage"));
const AdminSalesPage = lazy(() => import("./pages/admin/SalesPage"));
const SalesContactsPage = lazy(() => import("./pages/admin/SalesContactsPage"));
const SalesAnalyticsPage = lazy(() => import("./pages/admin/SalesAnalyticsPage"));
const SalesPipelinePage = lazy(() => import("./pages/admin/SalesPipelinePage"));
const AutomationsPage = lazy(() => import("./pages/admin/AutomationsPage"));
const AutomationBuilderPage = lazy(() => import("./pages/admin/AutomationBuilderPage"));
const SystemHealthPage = lazy(() => import("./pages/admin/SystemHealthPage"));
const OnboardingFlowsPage = lazy(() => import("./pages/admin/OnboardingFlowsPage"));
const MyTickets = lazy(() => import("./pages/support/MyTickets"));

const LinksPage = lazy(() => import("./pages/LinksPage"));
const Policies = lazy(() => import("./pages/Policies"));
const OnboardingTest = lazy(() => import("./pages/OnboardingTest"));
const ContentLibrary = lazy(() => import("./pages/dashboard/ContentLibrary"));
const ContentDocumentView = lazy(() => import("./pages/dashboard/ContentDocumentView"));

const RecruitingPage = lazy(() =>
 import("./pages/dashboard/RecruitingPage").then((m: any) => ({ default: m.default || m.RecruitingPage }))
);

const SafeDashboard = lazy(() => import("./pages/dashboard/SafeDashboard"));
const LeadershipDashboard = lazy(
 () => import("./pages/dashboard/LeadershipDashboard"),
);
const LeadCalendar = lazy(() => import("./pages/profile/LeadCalendar"));
const AcceptInvitePage = lazy(() => import("./pages/invite/AcceptInvitePage"));
const JoinPage = lazy(() => import("./pages/join/JoinPage"));
const LicensingPortal = lazy(() => import("./pages/licensing/LicensingPortal"));
const ProfilePage = lazy(() => import("./pages/dashboard/ProfilePage"));
const ApiKeysPage = lazy(() => import("./pages/settings/api-keys"));
const PersonalAccessTokensPage = lazy(() => import("./pages/settings/personal-access-tokens"));
const OAuthDevicePage = lazy(() => import("./pages/oauth/device"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));
const MicrosoftOAuthCallback = lazy(() => import("./pages/MicrosoftOAuthCallback"));


const queryClient = new QueryClient({
 defaultOptions: {
 queries: {
 staleTime: 5 * 60 * 1000, // 5 minutes
 },
 },
});

// Loading component for suspense fallback
const PageLoader = () => (
 <div className="min-h-screen flex items-center justify-center">
 <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
 </div>
);

// Component that handles OAuth and pre-warming
const AppWithTracking = () => {
 useGoogleOAuth(); // Handle Google OAuth token storage
 useMicrosoftOAuth(); // Handle Microsoft OAuth token storage
 useActivityHeartbeat(); // Track user activity for "Active Now" metrics

 // Pre-warm profile RPC on app mount (non-blocking) and clean up boot param
 React.useEffect(() => {
 // Phase 4: Call repair function on app startup (non-blocking, gated by feature flag)
 const repairOnboardingState = async () => {
 try {
 const enableRepair = import.meta.env.VITE_ENABLE_REPAIR_ON_BOOT === 'true';
 if (!enableRepair) return; // Do not call repair by default

 console.log('🔧 [App] Calling onboarding state repair...');
 const { supabase } = await import('@/integrations/supabase/client');
 
 // Optional: only run for admins to avoid spamming logs
 const { data: roleData, error: roleErr } = await supabase.rpc('get_user_app_role', { _user_id: (await supabase.auth.getUser()).data.user?.id });
 if (roleErr) {
 console.warn('⚠️ [App] Repair skipped (role check failed):', roleErr);
 return;
 }
 const allowed = roleData === 'tenant_admin' || roleData === 'global_admin';
 if (!allowed) return;

 const { data, error } = await supabase.functions.invoke('ef_repair_onboarding_state');
 if (error) {
 console.warn('⚠️ [App] Repair function warning:', error);
 } else if (data?.repaired > 0) {
 console.log(`✅ [App] Repaired ${data.repaired} profiles`);
 }
 } catch (err) {
 console.warn('⚠️ [App] Repair function error (non-critical):', err);
 }
 };

 const prewarmProfile = async () => {
 try {
 const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
 if (session?.user) {
 console.info('🔥 [App] Pre-warming profile RPC for user:', session.user.id);
 const { supabase } = await import('@/integrations/supabase/client');
 const { data, error } = await supabase.rpc('ensure_profile_and_access');
 if (error) {
 console.warn('⚠️ [App] Pre-warm RPC failed (non-critical):', error);
 } else {
 console.info('✅ [App] Profile pre-warmed:', data);
 }
 }
 } catch (err) {
 console.warn('⚠️ [App] Pre-warm exception (non-critical):', err);
 }
 };
 
 // Run repair in background
 repairOnboardingState();
 prewarmProfile();
 
 // Clean up ?boot=1 param from URL if present
 if (window.location.search.includes('boot=1')) {
 console.info('🧹 [App] Removing boot=1 from URL');
 const url = new URL(window.location.href);
 url.searchParams.delete('boot');
 window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
 }
 }, []);

 return (
 <FeedbackProvider>
 <FeedbackWidget />
 <ErrorBoundary>
 <Suspense fallback={<PageLoader />}>
 <Routes>
 <Route path="/" element={<Index />} />
 <Route path="/auth" element={<Auth />} />
 <Route path="/login" element={<Auth />} />
 <Route path="/reset-password" element={<ResetPassword />} />
 <Route path="/links" element={<LinksPage />} />
 <Route path="/policies" element={<Policies />} />
 <Route path="/privacy" element={<Policies />} />
 <Route path="/test-onboarding" element={<OnboardingTest />} />
 
 <Route path="/dashboard" element={<Dashboard />}>
 <Route index element={<RoleRouter />} />
 <Route path="safe" element={<SafeDashboard />} />

 {/* Core MVP Features */}
 <Route path="chat" element={<ChatPage />} />
 <Route path="sales" element={<SalesPage />} />
 <Route path="training/*" element={<TrainingHub />} />
 <Route path="library" element={<ContentLibrary />} />
 <Route path="library/document/:id" element={<ContentDocumentView />} />
 <Route path="pipeline" element={<RecruitingPage />} />
 <Route path="recruiting" element={<RecruitingPage />} />
 <Route path="recruiting/pipeline" element={<RecruitingPage />} />
 <Route path="recruiting/chat" element={<RecruitingChat />} />
 <Route path="recruiting/leaderboard" element={<Leaderboard />} />
 <Route path="recruiting/management" element={<RoleRouteGuard allowedRoles={["manager", "tenant_admin", "global_admin"]}><TeamManagementPage /></RoleRouteGuard>} />
 <Route path="recruiting/management/tree" element={<RoleRouteGuard allowedRoles={["manager", "tenant_admin", "global_admin"]}><RecruitmentTreePage /></RoleRouteGuard>} />
 <Route path="recruiting/management/:memberId" element={<RoleRouteGuard allowedRoles={["manager", "tenant_admin", "global_admin"]}><TeamMemberDetailPage /></RoleRouteGuard>} />
 <Route path="team" element={<RoleRouteGuard allowedRoles={["manager", "tenant_admin", "global_admin"]}><TeamPage /></RoleRouteGuard>} />
 <Route path="team/view" element={<RoleRouteGuard allowedRoles={["manager", "tenant_admin", "global_admin"]}><TeamViewPage /></RoleRouteGuard>} />
 <Route path="team/documents" element={<RoleRouteGuard allowedRoles={["manager", "tenant_admin", "global_admin"]}><TeamDocumentsPage /></RoleRouteGuard>} />
 <Route path="profile" element={<ProfilePage />} />
 <Route path="settings/api-keys" element={<RoleRouteGuard allowedRoles={["tenant_admin", "global_admin"]}><ApiKeysPage /></RoleRouteGuard>} />
 <Route path="settings/personal-access-tokens" element={<PersonalAccessTokensPage />} />
 <Route path="oauth/device" element={<OAuthDevicePage />} />
 <Route path="calendar" element={<CalendarPage />} />
 <Route path="profile/lead-calendar" element={<LeadCalendar />} />

 {/* Admin/Manager features */}
 <Route path="import" element={<ImportPage />} />
 <Route path="admin/onboard" element={<BulkOnboardPage />} />
              <Route path="template-requests" element={<TemplateRequestsPage />} />
 <Route path="email-test" element={<EmailTestPage />} />
 <Route path="users" element={<UsersPage />} />
 <Route path="admin/audit" element={<Navigate to="/dashboard/admin/system-health" replace />} />
 <Route path="admin/messages" element={<MessageHistoryPage />} />
 <Route path="admin/feedback" element={<FeedbackManagement />} />
 <Route path="admin/billing" element={<BillingManagementPage />} />
 <Route path="admin/billing/:tenantId" element={<TenantBillingDetailPage />} />
 <Route path="admin/sales" element={<AdminSalesPage />} />
 <Route path="admin/sales/contacts" element={<SalesContactsPage />} />
 <Route path="admin/sales/analytics" element={<SalesAnalyticsPage />} />
 <Route path="admin/sales/pipeline" element={<SalesPipelinePage />} />
 <Route path="admin/automations" element={<AutomationsPage />} />
 <Route path="admin/automations/:id" element={<AutomationBuilderPage />} />
 <Route path="admin/system-health" element={<SystemHealthPage />} />
 <Route path="admin/onboarding-flows" element={<RoleRouteGuard allowedRoles={["tenant_admin", "global_admin"]}><OnboardingFlowsPage /></RoleRouteGuard>} />
 <Route path="admin/isps" element={<IspManagementPage />} />
 <Route path="support/tickets" element={<MyTickets />} />
 <Route path="tenants" element={<TenantsPage />} />
 
 {features.leadership_dashboard && (
 <>
 <Route path="leadership" element={<LeadershipDashboard />} />
 <Route path="leadership/:nodeId" element={<LeadershipDashboard />} />
 </>
 )}
 </Route>
 <Route path="/invite/accept" element={<AcceptInvitePage />} />
 <Route path="/join" element={<JoinPage />} />
 <Route path="/licensing" element={<LicensingPortal />} />
 <Route path="/auth/callback" element={<OAuthCallback />} />
 <Route path="/auth/microsoft-callback" element={<MicrosoftOAuthCallback />} />
 <Route path="*" element={<NotFound />} />
 </Routes>
 </Suspense>
 </ErrorBoundary>
 </FeedbackProvider>
 );
};

const App = () => {
 return (
 <QueryClientProvider client={queryClient}>
 <ThemeProvider>
 <SupabaseAuthProvider>
 <InactivityTimeoutProvider>
 <TenantProvider>
 <VerticalProvider>
 <RoleViewProvider>
 <TooltipProvider>
 <Toaster />
 <Sonner />
 <BrowserRouter>
 <AppWithTracking />
 </BrowserRouter>
 </TooltipProvider>
 </RoleViewProvider>
 </VerticalProvider>
 </TenantProvider>
 </InactivityTimeoutProvider>
 </SupabaseAuthProvider>
 </ThemeProvider>
 </QueryClientProvider>
 );
};

export default App;


